import { useCallback, useEffect, useMemo } from 'react';

import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { chartElements } from '../components/reports/useChartShare';
import { normalizeId } from '../utils/ids';

/**
 * What a chart's frame needs from its card, for charts drawn away from their
 * own page (charts A7: Home's chart pins and the Overview's dashboard — the
 * dashboard page reads the same): the card's name for the notes' words ("12
 * sales have no Amount…") and the group and split elements, so a choice keeps
 * its own colour (ruled q15) — with every element of the card (`list`) and of
 * its chain unmerged (`chain`), which name a spec field that holds an id
 * (KPR-238 P6; useChartShare chartElements). Read from the app-wide card definitions; a card
 * this session does not hold yet is asked for once (noteMissingCards), and a
 * chart whose card is not readable simply draws without them.
 *
 * `charts` is a list of chart faces (`{ _id, spec }`); returns
 * `chartContext(chart) → { cardName, elements } | null`.
 */
export default function useChartContexts(charts) {
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const noteMissingCards = cardDefinitions?.noteMissingCards;
	const list = useMemo(() => (Array.isArray(charts) ? charts.filter(Boolean) : []), [charts]);
	const cardIds = useMemo(() => [...new Set(list.map((chart) => normalizeId(chart.spec?.card_id)).filter(Boolean))], [list]);
	const missingKey = cardIds.filter((id) => !cardsById[id]).join(',');
	useEffect(() => {
		if (typeof noteMissingCards === 'function' && missingKey) noteMissingCards(missingKey.split(','));
	}, [missingKey, noteMissingCards]);
	const contexts = useMemo(() => {
		const map = new Map();
		list.forEach((chart) => {
			const cardId = normalizeId(chart.spec?.card_id);
			const card = cardId ? cardsById[cardId] : null;
			const id = normalizeId(chart._id);
			if (!card || map.has(id)) return;
			map.set(id, {
				cardName: card.name,
				elements: chartElements(chart, getElementsForCard, { cardsById, elementSetsById }),
			});
		});
		return map;
	}, [list, cardsById, elementSetsById, getElementsForCard]);
	return useCallback((chart) => contexts.get(normalizeId(chart && chart._id)) || null, [contexts]);
}
